import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, AlertCircle, ChevronDown, X } from "lucide-react";
import { api } from "@/api/client";

interface RoleItem {
  id: string;
  name: string;
  description?: string;
}

const createSchema = z
  .object({
    username: z.string().min(3, "Username minimal 3 karakter").max(100),
    full_name: z.string().min(2, "Nama minimal 2 karakter").max(255),
    email: z.string().email("Email tidak valid"),
    nip: z.string().max(30).optional().or(z.literal("")),
    password: z.string().min(8, "Password minimal 8 karakter"),
    confirm_password: z.string(),
    role_ids: z.array(z.string().uuid()).min(1, "Pilih minimal 1 role"),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: "Password tidak cocok",
    path: ["confirm_password"],
  });

type CreateFormValues = z.infer<typeof createSchema>;

const ROLE_COLORS: Record<string, string> = {
  ADMIN: "border-red-200 bg-red-50 text-red-700",
  LEADER: "border-purple-200 bg-purple-50 text-purple-700",
  AUDITOR: "border-yellow-200 bg-yellow-50 text-yellow-700",
  MANAGER: "border-blue-200 bg-blue-50 text-blue-700",
  TREASURER: "border-green-200 bg-green-50 text-green-700",
  USER: "border-gray-200 bg-gray-50 text-gray-700",
  GUEST: "border-slate-200 bg-slate-50 text-slate-600",
};

function MultiSelectRoles({
  roles,
  loading,
  value,
  onChange,
}: {
  roles: RoleItem[];
  loading: boolean;
  value: string[];
  onChange: (val: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedRoles = roles.filter((r) => value.includes(r.id));
  const unselectedRoles = roles.filter((r) => !value.includes(r.id));

  return (
    <div ref={ref} className="relative">
      {/* Selected tags + trigger */}
      <div
        onClick={() => setOpen(!open)}
        className="flex flex-wrap items-center gap-1.5 min-h-[42px] w-full rounded-lg border border-gray-300 px-3 py-2 text-sm cursor-pointer focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500"
      >
        {selectedRoles.length === 0 && (
          <span className="text-gray-400">Pilih role...</span>
        )}
        {selectedRoles.map((role) => (
          <span
            key={role.id}
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${ROLE_COLORS[role.name] || "bg-gray-100 text-gray-700"}`}
          >
            {role.name}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(value.filter((id) => id !== role.id));
              }}
              className="hover:opacity-70"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <ChevronDown className={`h-4 w-4 text-gray-400 ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg py-1 max-h-60 overflow-auto">
          {loading ? (
            <p className="px-3 py-2 text-sm text-gray-400">Memuat role...</p>
          ) : unselectedRoles.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-400">Semua role sudah dipilih</p>
          ) : (
            unselectedRoles.map((role) => (
              <button
                key={role.id}
                type="button"
                onClick={() => {
                  onChange([...value, role.id]);
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between"
              >
                <span className="font-medium">{role.name}</span>
                {role.description && (
                  <span className="text-xs text-gray-400">{role.description}</span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function UserCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: roles = [], isLoading: rolesLoading } = useQuery<RoleItem[]>({
    queryKey: ["roles"],
    queryFn: () => api.get("/roles/"),
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { role_ids: [] },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateFormValues) =>
      api.post("/users/", {
        username: data.username,
        full_name: data.full_name,
        email: data.email,
        nip: data.nip || undefined,
        password: data.password,
        role_ids: data.role_ids,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      navigate("/users");
    },
  });

  const onSubmit = (data: CreateFormValues) => createMutation.mutate(data);

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate("/users")}
          className="rounded-lg border border-gray-300 p-2 hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tambah Pengguna</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Buat akun pengguna baru
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {createMutation.isError && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 p-4 mb-4 text-red-700">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm">
              {(createMutation.error as any)?.response?.data?.detail ||
                "Gagal membuat pengguna. Silakan coba lagi."}
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username <span className="text-red-500">*</span>
            </label>
            <input
              {...register("username")}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="username"
            />
            {errors.username && (
              <p className="mt-1 text-xs text-red-600">
                {errors.username.message}
              </p>
            )}
          </div>

          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nama Lengkap <span className="text-red-500">*</span>
            </label>
            <input
              {...register("full_name")}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="Nama lengkap pengguna"
            />
            {errors.full_name && (
              <p className="mt-1 text-xs text-red-600">
                {errors.full_name.message}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              {...register("email")}
              type="email"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="email@contoh.com"
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-600">
                {errors.email.message}
              </p>
            )}
          </div>

          {/* NIP */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              NIP
            </label>
            <input
              {...register("nip")}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="Nomor Induk Pegawai..."
            />
          </div>

          {/* Password */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                {...register("password")}
                type="password"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="Minimal 8 karakter"
              />
              {errors.password && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.password.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Konfirmasi Password <span className="text-red-500">*</span>
              </label>
              <input
                {...register("confirm_password")}
                type="password"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="Ulangi password"
              />
              {errors.confirm_password && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.confirm_password.message}
                </p>
              )}
            </div>
          </div>

          {/* Roles */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role <span className="text-red-500">*</span>
            </label>
            <Controller
              name="role_ids"
              control={control}
              render={({ field }) => (
                <MultiSelectRoles
                  roles={roles}
                  loading={rolesLoading}
                  value={field.value || []}
                  onChange={field.onChange}
                />
              )}
            />
            {errors.role_ids && (
              <p className="mt-1 text-xs text-red-600">
                {errors.role_ids.message}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate("/users")}
              className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={isSubmitting || createMutation.isPending}
              className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting || createMutation.isPending
                ? "Menyimpan..."
                : "Buat Pengguna"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
